import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Zap, CheckCircle2, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";

export const ConnectCard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/connect/status")
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus(null))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    if (params.get("connect")) {
      // returned from Stripe onboarding — clean the URL
      window.history.replaceState({}, "", "/dashboard");
    }
  }, [load]);

  const startOnboarding = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/connect/onboard", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start onboarding");
      setStarting(false);
    }
  };

  const enabled = status?.charges_enabled;
  const pending = status?.connected && !status?.charges_enabled;

  return (
    <div className="mt-4 rounded-2xl border border-white/10 bg-[#0F0F0F] p-6 sm:p-8" data-testid="connect-card">
      <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="max-w-md">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
            <Zap className="h-4 w-4 text-[#00E676]" /> Instant payouts
          </div>
          {loading ? (
            <div className="mt-3 flex items-center gap-2 text-zinc-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Checking status…
            </div>
          ) : enabled ? (
            <>
              <h3 className="mt-3 flex items-center gap-2 font-display text-xl font-semibold text-[#00E676]">
                <CheckCircle2 className="h-5 w-5" /> Connected — you get paid instantly
              </h3>
              <p className="mt-2 leading-relaxed text-zinc-400">
                Tips are sent straight to your bank via Stripe, and our 3% fee is split automatically —
                no cash-out needed.
              </p>
            </>
          ) : pending ? (
            <>
              <h3 className="mt-3 flex items-center gap-2 font-display text-xl font-semibold text-amber-400">
                <AlertCircle className="h-5 w-5" /> Onboarding incomplete
              </h3>
              <p className="mt-2 leading-relaxed text-zinc-400">
                Finish your Stripe setup to unlock instant automatic payouts.
              </p>
            </>
          ) : (
            <>
              <h3 className="mt-3 font-display text-xl font-semibold">Get paid instantly, automatically.</h3>
              <p className="mt-2 leading-relaxed text-zinc-400">
                Connect a Stripe account and every tip lands in your bank instantly — the 3% fee is split
                automatically, so you never have to request a cash-out.
              </p>
            </>
          )}
        </div>

        {!loading && !enabled && (
          <button
            data-testid="connect-onboard-btn"
            onClick={startOnboarding}
            disabled={starting}
            className="flex items-center gap-2 rounded-full bg-[#00E676] px-7 py-3.5 font-bold text-black transition-transform active:scale-95 disabled:opacity-60"
          >
            {starting ? <Loader2 className="h-5 w-5 animate-spin" /> : pending ? "Finish setup" : "Set up instant payouts"}
          </button>
        )}
      </div>
    </div>
  );
};
